<script setup lang="ts">
import { MMIcon, MMLink, MMText } from '@mm-frontend/mm-web-ui-kit';
import type { CategoryMM } from '~/entities/category';
import { useCategoriesTracker } from '../trackers';

const CONFIG = {
  MAX_NESTING_LEVEL: 1,
  MAX_CHILDREN_COUNT: 4,
} as const;

const props = withDefaults(
  defineProps<{
    category: CategoryMM;
    level?: number;
  }>(),
  {
    level: 0,
  }
);

const { trackCategoryClick } = useCategoriesTracker();
const isExpanded = ref(false);

const font = computed(() => {
  switch (props.level) {
    case 0:
      return 'Headline M/Bold';
    case 1:
      return 'Body M/Medium';
    default:
      return 'Body M/Regular';
  }
});

const childCategories = computed(() => {
  const allChildren = props.category.childrens || [];

  if (
    props.level === 1 &&
    !isExpanded.value &&
    allChildren.length > CONFIG.MAX_CHILDREN_COUNT + 1
  ) {
    return allChildren.slice(0, CONFIG.MAX_CHILDREN_COUNT);
  }

  return allChildren;
});

const totalChildrenCount = computed(() => {
  return props.category.childrens?.length || 0;
});

const shouldShowExpandButton = computed(() => {
  return (
    props.level === 1 &&
    totalChildrenCount.value > CONFIG.MAX_CHILDREN_COUNT + 1 &&
    !isExpanded.value
  );
});

const hiddenItemsCount = computed(() => {
  return totalChildrenCount.value - CONFIG.MAX_CHILDREN_COUNT;
});

const shouldShowChildren = computed(() => {
  return childCategories.value.length > 0 && props.level <= CONFIG.MAX_NESTING_LEVEL;
});

const handleExpandClick = (): void => {
  isExpanded.value = true;
};
</script>

<template>
  <div :class="['category-childrens', `category-childrens--level-${level}`]">
    <MMLink
      :to="category.url"
      :aria-label="`Категория ${category.title}`"
      :font="font"
      :label="category.title"
      design="none"
      class="category-childrens__title"
      @click="trackCategoryClick(category)"
    />

    <div v-if="shouldShowChildren" class="category-childrens__inner">
      <CategoryChildrens
        v-for="childCategory in childCategories"
        :key="childCategory.id"
        :category="childCategory"
        :level="level + 1"
      />

      <button
        v-if="shouldShowExpandButton"
        type="button"
        class="category-childrens__expand-button"
        @click="handleExpandClick"
      >
        <MMText font="Body M/Regular">Ещё {{ hiddenItemsCount }}</MMText>
        <MMIcon name="chevron" :height="16" :width="16" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="stylus">
.category-childrens
  &__expand-button
    display flex
    align-items center
    gap 4px
    cursor pointer
    margin-top 2px
    background none
    border none
    padding 0
    font inherit
    color: var(--text-secondary)
    transition color .2s

    .mm-text, .mm-icon
      color inherit

    &:hover
      color var(--text-accented)

  &__title
    color: var(--text-secondary)

    &:hover
      color: var(--text-accented)

  &--level-0
    > .category-childrens
      &__title
        margin-bottom 24px
        color: var(--text-primary)

        &:hover
          color: var(--text-accented)

      &__inner
        columns 3
        column-gap 24px
        column-fill balance
        height auto

        @media (max-width $pre-desktop)
          columns 2

        > *
          break-inside avoid
          margin-bottom 24px
          display inline-block
          width 100%

  &--level-1
    > .category-childrens
      &__title
        margin 8px 0 14px
        color: var(--text-primary)

        &:hover
          color: var(--text-accented)

      &__inner
        display flex
        flex-direction column
        gap 8px
</style>
